import React, { useEffect } from 'react';
import {
    Layers,
    Calculator,
    Gift,
    Scale,
    Truck,
    Users,
    Store,
    AlertTriangle,
    Search,
    FileText,
    MessageCircle,
    FolderOpen,
    Mic
} from 'lucide-react';

const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        },
        amber: {
            bg: "bg-amber-900/10",
            border: "border-amber-500/30",
            iconBg: "bg-amber-500/10",
            iconColor: "text-amber-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

const NumRow = ({ n, h, s, meta }) => (
    <div className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
        <div className="text-xl sm:text-2xl font-black text-indigo-400/80 shrink-0 w-12 sm:w-14 tabular-nums pt-0.5">{n}</div>
        <div>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
                <h3 className="text-base sm:text-lg font-black text-white leading-snug">{h}</h3>
                {meta && (
                    <span className="text-[9px] sm:text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border text-amber-300 border-amber-500/40 bg-amber-500/10">
                        {meta}
                    </span>
                )}
            </div>
            <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{s}</p>
        </div>
    </div>
);

export default function PranayPlaybookPlan() {

    useEffect(() => {
        document.title = "Pranay Jham | Playbook Plan";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Pranay Jham</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Playbook</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Your free resource, and what we need from you
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        The First 100<br />Orders Playbook.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-6 font-light">
                        The most complete guide an early Indian D2C founder can get, given away free.<br className="hidden sm:block" /> People comment one word on your reel, and it lands in their DMs.
                    </p>
                </section>

                {/* WHAT THIS IS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">What we are building</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Not a PDF.<br />A working kit.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Most free guides are a list of tips that leave the reader with homework. This one hands them the actual instruments, so they can act the same day.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Layers} heading="8 modules, 23 chapters" subheading="the whole zero to one journey, from checking whether your product can make money to reading your first hundred orders" accent="indigo" />
                        <InfoCard icon={Calculator} heading="Tools, not just words" subheading="a will-this-make-money calculator, a pricing worksheet, a channel comparison sheet, a supplier script pack, an order tracker" accent="indigo" />
                        <InfoCard icon={Gift} heading="Released module by module" subheading="each one with its own comment word, so every release is a fresh reason for people to reach out, then compiled into the full playbook" accent="indigo" />
                    </div>
                </section>

                {/* WHAT WE FOUND */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">What we have already found</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Two hundred sourced<br />numbers, so far.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            We have done the research legwork already. Every figure carries its source and its date, and anything we could not verify is marked as unverified rather than guessed.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Scale} heading="Category sets the ceiling" subheading="published filings show Indian D2C gross margins running from about 30% to about 71% depending on the category · most founders pick a price before they know which band they are in" accent="rose" />
                        <InfoCard icon={Truck} heading="Returns decide who survives" subheading="around 14% of Indian orders come back overall · but cash on delivery reaches 58% at festive peak against under 15% on prepaid" accent="rose" />
                        <InfoCard icon={Users} heading="Most buyers never come back" subheading="roughly 84 out of every 100 Indian online buyers are first-time · the first purchase has to pay for itself on its own" accent="rose" />
                        <InfoCard icon={Store} heading="Quick commerce is not cheap" subheading="platforms retain roughly a third of the selling price · that changes whether it belongs anywhere near your first hundred orders" accent="rose" />
                        <InfoCard icon={AlertTriangle} heading="A rule change is coming" subheading="from January 2027 discount claims on Indian ecommerce face new disclosure requirements · we are having the exact wording read by a lawyer before it goes in the guide" accent="rose" />
                        <InfoCard icon={Search} heading="The number nobody publishes" subheading="not one platform or report anywhere states how long a new brand takes to reach its first hundred orders · you are the only person who can answer that" accent="rose" />
                    </div>
                </section>

                {/* THE MODULES */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The structure</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Eight modules,<br />in the order it happens.
                        </h2>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        <NumRow n="00" h="Before you spend a rupee" s="The five numbers that decide whether a product can make money, and how to test demand before you commit to inventory." />
                        <NumRow n="01" h="Price" s="Pricing backwards from margin instead of forwards from competitors, the full India cost stack, and why launching on discount traps you." />
                        <NumRow n="02" h="Product and supply" s="Your first manufacturer, what to negotiate, how much to make first, and packaging that survives Indian logistics." />
                        <NumRow n="03" h="Place" s="Own site against marketplace against quick commerce, with the real economics of each, including offline." />
                        <NumRow n="04" h="The first 10 orders" s="Friends and family done right, how that early data lies to you, and getting your first ten strangers." />
                        <NumRow n="05" h="Orders 11 to 100" s="Channels that work on zero budget, content as distribution, your first spend on reach, and the cash on delivery decision." />
                        <NumRow n="06" h="Operations that do not break" s="Shipping, returns, defending against RTO, and customer service when it is still just you." />
                        <NumRow n="07" h="The decision point" s="Reading what your first hundred orders are telling you, and deciding whether to stop, fix or scale." />
                    </div>
                </section>

                {/* HOW WE WORK */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">How we work</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            We do the legwork.<br />You correct us.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You will never have to write anything or look anything up. This costs you about an hour at a time instead of ten.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Search} heading="We research and draft" subheading="fees, rates, rules, benchmarks · we hold the current numbers so you never have to recall them, and stale memory never reaches the page" accent="emerald" />
                        <InfoCard icon={MessageCircle} heading="You react and correct" subheading="we read you what we found and you tell us where it is wrong · reacting to a real claim is far easier than remembering, and your disagreements are the best part" accent="emerald" />
                        <InfoCard icon={FileText} heading="Your judgment is the product" subheading="what to skip, what order, what you would never repeat · research can never supply that, and it is the reason this cannot be copied off you" accent="emerald" />
                    </div>
                </section>

                {/* WHAT WE NEED FROM YOU */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">What we need from you</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Five steps.<br />About two hours.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            That is the first module done. Each one after costs you around 45 minutes.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        <NumRow n="01" meta="15 min" h="Dig out your old records" s="First order sheet, first invoice, an early expense sheet, the first supplier or distributor thread, screenshots of early dashboards, any old pricing calculation. Photos are fine, do not organise it. One real document beats an hour of trying to remember." />
                        <NumRow n="02" meta="60 min" h="One call with us" s="We ask, you talk. Nothing to prepare, nothing to type. Most questions are us checking our research against what you actually did. Keep your phone recording on your side, because your answers double as reel clips." />
                        <NumRow n="03" meta="30 min" h="Mark up the draft" s="Three things: what is wrong or out of date, what you would add that nobody else writes, and what you actually did differently. Be blunt. Scribbles and voice notes are perfect." />
                        <NumRow n="04" meta="5 min" h="Pick two things" s="The title, from three options we send you. And the one word people comment to unlock it. Short and easy to spell." />
                        <NumRow n="05" meta="2 min" h="Connect the DM tool" s="One authorisation on your Instagram so the guide sends itself. We set up the rest and test it with real comments before it goes near your audience." />
                    </div>
                </section>

                {/* THE FIVE NUMBERS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">On the call</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Six things only<br />you can tell us.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Scale} heading="Your gross margin at launch" subheading="where your product actually sat, and whether the survival line people quote holds up in real life" accent="amber" />
                        <InfoCard icon={Truck} heading="What shipping really cost" subheading="published data and agency claims are far apart on this · your real per-order number settles it" accent="amber" />
                        <InfoCard icon={FolderOpen} heading="Your return rate on COD" subheading="what you actually saw in the first hundred orders, and what you did about it" accent="amber" />
                        <InfoCard icon={Search} heading="How long 100 orders took" subheading="the number the guide is named after, and the one nobody anywhere publishes" accent="amber" />
                        <InfoCard icon={Calculator} heading="Your cost breakdown" subheading="if you are willing to let us publish it, named or anonymous, it becomes the only first-party number in the guide" accent="amber" />
                        <InfoCard icon={Mic} heading="What you would never repeat" subheading="the mistakes, and what you watch new founders get wrong every single time" accent="amber" />
                    </div>
                </section>

                {/* THE RULE */}
                <section>
                    <div className="max-w-3xl mx-auto">
                        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-900/10 p-6 sm:p-8 text-center">
                            <div className="text-emerald-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-4">The rule we hold</div>
                            <p className="text-sm sm:text-base text-white/60 font-light leading-relaxed">
                                Every chapter has to carry at least one thing only you could have said. If a chapter comes back from you with no marks on it, it goes back into the draft pile instead of shipping. A chapter that could have been written by anyone is not worth your name on it.
                            </p>
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pt-4 sm:pt-8">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight mb-6">
                        It starts with one call.
                    </h2>
                    <p className="text-base sm:text-lg text-white/55 max-w-xl mx-auto font-light leading-relaxed mb-10">
                        Pick an hour that suits you, dig out whatever old records you can find, and we will take it from there.
                    </p>
                    <a
                        href="mailto:suresh@socialseo.in"
                        className="inline-block bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        suresh@socialseo.in
                    </a>
                </section>

                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
